<template>
  <div class="email-verification-success">
    <div class="success-container">
      <!-- Success Icon -->
      <div class="success-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2"/>
          <path d="M9 12l2 2 4-4" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      
      <!-- Main Heading -->
      <h1 class="success-title">Email Verified</h1>
      
      <!-- Descriptive Message -->
      <p class="success-message">Your email address was successfully verified.</p>
      
      <!-- Optional: Continue Button -->
      <div v-if="showContinueButton" class="continue-section">
        <button @click="handleContinue" class="continue-btn">
          Continue to Dashboard
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useTutorRegistrationStorage } from '@/composables/useRegistrationStorage';

const router = useRouter();
const showContinueButton = ref(false);
const { clearTutorRegistration } = useTutorRegistrationStorage();

// Props
interface Props {
  autoRedirect?: boolean;
  redirectDelay?: number;
  redirectPath?: string;
}

const props = withDefaults(defineProps<Props>(), {
  autoRedirect: true,
  redirectDelay: 3000,
  redirectPath: '/login'
});

// Handle continue button click
const handleContinue = () => {
  router.push(props.redirectPath);
};

// Auto redirect after delay
onMounted(() => {
  // Clear the registration data from sessionStorage since email is now verified
  clearTutorRegistration();
  console.log('✅ Cleared tutor registration data after email verification');
  
  if (props.autoRedirect) {
    setTimeout(() => {
      router.push(props.redirectPath);
    }, props.redirectDelay);
  } else {
    // Show continue button if auto redirect is disabled
    showContinueButton.value = true;
  }
});
</script>

<style scoped>
.email-verification-success {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 20px;
}

.success-container {
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 32px auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon svg {
  width: 100%;
  height: 100%;
}

.success-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

.success-message {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

.continue-section {
  margin-top: 32px;
}

.continue-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

.continue-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.continue-btn:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 480px) {
  .success-container {
    max-width: 100%;
  }
  
  .success-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 24px;
  }
  
  .success-title {
    font-size: 28px;
  }
  
  .success-message {
    font-size: 14px;
  }
  
  .continue-btn {
    padding: 10px 20px;
    font-size: 14px;
  }
}
</style>
